import React, { useMemo } from 'react';
import { func, shape, oneOfType, arrayOf, node, number } from 'prop-types';
import { useQuery } from '@apollo/react-hooks';
import { extractRootKeys } from 'components/utils/search';
import useVelcro from 'components/Form/Filter/useVelcro';
import useTimeout from 'utils/useTimeout';
import { toParams } from 'utils/history';
import useShortcuts from 'utils/shortcuts/useShortcuts';
import { useLocation, useNavigate } from 'react-router-dom';
import Context from './Context';
import useIdsHidrator from './useIdsHidrator';
import { getIndexBasedSkip, addSearchParams } from './utils';
import defaultSchema from './defaultSchema';

const merge = (initialVariables, initialValues) => {
  if (!initialVariables && !initialValues) {
    return null;
  }

  if (initialValues && initialVariables) {
    return {
      ...initialValues,
      ...initialVariables,
      f: {
        ...(initialValues.f || {}),
        ...(initialVariables.f || {}),
      },
    };
  }

  return initialValues || initialVariables;
};

function NavigationProvider({
  buildUrl,
  children,
  id,
  query,
  schema,
  variables: initialVariables,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { search } = location;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rootKeys = useMemo(() => extractRootKeys(query), []);
  const { initialValues } = useVelcro({
    schema,
    search,
    rootKeys,
    ignoreUpdates: true,
  });

  const shouldRenderChildren = useTimeout(300, id);

  const { data, loading, variables, fetchMore, refetch } = useQuery(query, {
    variables: merge(initialVariables, initialValues),
    skip: !id,
    notifyOnNetworkStatusChange: true,
  });

  const params = toParams(search);
  const { prevId, nextId, index } = useIdsHidrator({
    id,
    params,
    fetchMore,
    variables,
    data,
  });

  function goTo(path) {
    if (!path) {
      return;
    }

    // Preserve the current state and add navigation info
    const navigationState = {
      ...(location.state || {}),
      from: location.pathname,
      previousPath: location.pathname,
      keepReferrer: true,
    };

    navigate(path, { state: navigationState });
  }

  const prev = prevId ? buildUrl(prevId) : null;
  const next = nextId ? buildUrl(nextId) : null;

  const prevSkip = prev ? getIndexBasedSkip(index - 1, variables.limit) : null;
  const nextSkip = next ? getIndexBasedSkip(index + 1, variables.limit) : null;

  const prevLink = addSearchParams(prev, prevSkip, params);
  const nextLink = addSearchParams(next, nextSkip, params);

  useShortcuts(
    [
      ['Right', () => goTo(nextLink)],
      ['Left', () => goTo(prevLink)],
    ],
    !nextLink && !prevLink,
  );

  const value = useMemo(
    () => ({
      prev: prevLink,
      next: nextLink,
      disabled: loading,
      refetch,
    }),
    [prevLink, nextLink, loading, refetch],
  );

  return (
    <Context.Provider value={value}>
      {shouldRenderChildren && children}
    </Context.Provider>
  );
}

NavigationProvider.propTypes = {
  buildUrl: func.isRequired,
  children: oneOfType([node, arrayOf(node)]).isRequired,
  id: number,
  query: shape({}).isRequired,
  schema: shape({}),
  variables: shape({}),
};

NavigationProvider.defaultProps = {
  id: null,
  schema: defaultSchema,
  variables: null,
};

export default NavigationProvider;
